import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaAnchor, FaChartLine, FaRss, FaEdit, FaCheckSquare, FaGlobe, FaHistory, FaCheckCircle, FaHeart, FaHands, FaBalanceScale, FaUsers, FaLightbulb } from 'react-icons/fa';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock,FaGraduationCap } from 'react-icons/fa';

const testimonials = [
  {
    id: 1,
    quote: "Being part of the Illinois Interfaith Organization showed me how powerful respectful dialogue can be. I witnessed people from different faiths sit together, listen deeply, and leave with genuine understanding and hope.",
    name: "— Sarah Ahmed",
    role: "Community Volunteer",
    img: "/1.jpg"
  },
  {
    id: 2,
    quote: "The Adam & Eve Day celebration reminded me that beyond our differences, we truly are one human family. IIO creates spaces where unity feels real, not symbolic.",
    name: "— David Reynolds",
    role: "Community Member",
    img: "/2.jpg"
  },
  {
    id: 3,
    quote: "Illinois Interfaith Organization is doing meaningful work by bringing communities together with sincerity and respect. Their events build trust, encourage peace, and strengthen the social fabric of our neighborhoods.",
    name: "— Fatima Khan",
    role: "Interfaith Program Participant",
    img: "/3.jpg"
  }
];

const About = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const staff = [
    { name: "John Doe", role: "CEO & Founder", img: "/staff1.jpg" },
    { name: "Jane Smith", role: "Volunteer", img: "/staff1.jpg" },
    { name: "Mike Tyson", role: "Manager", img: "/staff1.jpg" },
    { name: "Sara Wilson", role: "Director", img: "/staff1.jpg" },
  ];

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const validate = () => {
    let tempErrors = {};
    if (!formData.name.trim()) tempErrors.name = "Name is required";
    if (!formData.email.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) tempErrors.email = "Invalid email format";
    if (!formData.subject) tempErrors.subject = "Subject is required";
    if (formData.message.length < 10) tempErrors.message = "Message must be at least 10 characters";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setSuccessMsg("Thank you! Your message has been sent successfully.");
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSuccessMsg(''), 5000);
      }, 2000);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">

      {/* 1. HERO SECTION */}
      <section
        className="relative h-[45vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('/Adam & Eve Day _22.jpg')` }}
      >
        <div className="text-center text-white">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-5xl md:text-6xl font-serif font-black uppercase tracking-tight"
          >
            About Us
          </motion.h1>
          <div className="h-1.5 w-20 bg-[#f9b50b] mx-auto mt-4"></div>

          <p className="mt-4 uppercase tracking-[0.3em] text-xs font-bold">
            <Link to="/" className="hover:text-[#f9b50b] text-gray-300 transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-white">Who We Are</span>
          </p>
        </div>
      </section>

      {/* 2. MISSION & STORY BOXES */}
      <section className="py-24 bg-white px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="p-12 text-center border-t-8 border-[#f9b50b] shadow-xl bg-white group hover:-translate-y-2 transition-all duration-300">
            <FaGlobe className="text-4xl text-[#f9b50b] mx-auto mb-6 group-hover:rotate-12 transition-transform" />
            <h3 className="text-3xl font-serif font-bold uppercase mb-6 text-[#221f1f]">Our Mission</h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              The mission of the Illinois Interfaith Organization is to promote unity and diversity by fostering understanding, dialogue, and collaboration across faiths and cultures. We seek to build inclusive communities that uphold human dignity and protect human rights.
            </p>
          </div>
          <div className="p-12 text-center border-t-8 border-[#f9b50b] shadow-xl bg-white group hover:-translate-y-2 transition-all duration-300">
            <FaHistory className="text-4xl text-[#f9b50b] mx-auto mb-6 group-hover:-rotate-12 transition-transform" />
            <h3 className="text-3xl font-serif font-bold uppercase mb-6 text-[#221f1f]">Our Story</h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              We envision a society where individuals from different faiths, cultures, and backgrounds come together in the spirit of respect and cooperation. The Illinois Interfaith Organization works to inspire collective action through meaningful engagement and community-building.
            </p>
          </div>
        </div>
      </section>

      {/* 3. NEW SECTION: OUR CORE VALUES */}
      <section className="py-24 bg-[#f9f9f9] px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-[#f9b50b] font-bold uppercase tracking-widest text-sm mb-2">Guided by Principles</h2>
            <h3 className="text-5xl font-serif font-bold text-[#221f1f] uppercase leading-tight">Our Core Values</h3>
            <div className="h-1 w-20 bg-[#f9b50b] mx-auto mt-6"></div>
            <p className="text-gray-500 max-w-3xl mx-auto mt-8 text-sm leading-relaxed italic">
              The Illinois Interfaith Organization is guided by core values that shape our mission, programs, and community engagement. We are committed to fostering unity across differences, honoring human dignity, and creating inclusive spaces where all voices are respected. Through collaboration and service, we work to strengthen relationships and promote peaceful coexistence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { title: "Unity in Diversity", icon: <FaUsers />, desc: "Embracing differences as a source of collective strength." },
              { title: "Mutual Respect", icon: <FaHeart />, desc: "Encouraging open dialogue grounded in listening and understanding." },
              { title: "Human Dignity", icon: <FaHands />, desc: "Upholding the inherent worth of every individual." },
              { title: "Inclusivity & Equity", icon: <FaBalanceScale />, desc: "Ensuring access, participation, and fairness for all." },
              { title: "Collaboration & Service", icon: <FaLightbulb />, desc: "Working together to create meaningful community impact." }
            ].map((value, idx) => (
              <div key={idx} className="bg-white p-8 border-b-4 border-[#f9b50b] shadow-sm hover:shadow-lg transition-all text-center group">
                <div className="text-[#f9b50b] text-3xl mb-4 flex justify-center group-hover:scale-110 transition-transform">{value.icon}</div>
                <h4 className="font-bold uppercase text-sm mb-3 text-[#221f1f]">{value.title}</h4>
                <p className="text-gray-500 text-[11px] leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. NEW SECTION: IMPACT & OUTCOMES */}
      <section className="py-24 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Side: Picture */}
            <div className="relative">
              <img 
                src="/Community Bash.jpeg" 
                className="w-full h-[550px] object-cover rounded-sm shadow-2xl" 
                alt="Community impact" 
              />
              <div className="absolute -bottom-6 -right-6 bg-[#f9b50b] p-8 hidden md:block shadow-lg">
                <p className="text-[#221f1f] font-black text-4xl">95%</p>
                <p className="text-[#221f1f] font-bold uppercase text-[10px] tracking-widest">Community Trust</p>
              </div>
            </div>

            {/* Right Side: Content Aligned to Image Height */}
            {/* Removed justify-between to pull elements together */}
            <div className="flex flex-col h-full"> 
              <div>
                <h2 className="text-[#f9b50b] font-bold uppercase tracking-widest text-sm mb-2">The Results</h2>
                <h3 className="text-5xl font-serif font-bold text-[#221f1f] uppercase leading-tight">Impact & Outcomes</h3>
                <div className="h-1 w-20 bg-[#f9b50b] mt-4"></div>
                {/* Reduced mt-6 to mt-4 to pull text up */}
                <p className="text-gray-600 leading-relaxed text-sm italic mt-4">
                  We measures success through lasting relationships, increased understanding, and positive social change. Our initiatives bring people together across faiths and backgrounds, fostering trust, cooperation, and shared purpose. The outcomes of our work extend beyond programs, creating sustainable community impact.
                </p>
              </div>

              {/* Impact points in 2 Columns - Added mt-6 here to control the specific gap */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {[
                  { t: "Stronger Interfaith Relationships", d: "Building trust through dialogue and shared experiences." },
                  { t: "Inclusive Community Engagement", d: "Creating welcoming spaces for diverse participation." },
                  { t: "Increased Civic Awareness", d: "Encouraging informed and compassionate social action." },
                  { t: "Sustainable Collaboration", d: "Inspiring ongoing partnerships and initiatives." },
                  { t: "Culture of Peace & Respect", d: "Advancing empathy, understanding, and coexistence." }
                ].map((impact, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 border border-gray-50 bg-[#fcfcfc] hover:bg-gray-50 transition-colors h-full">
                    <FaCheckCircle className="text-[#f9b50b] text-lg mt-1 shrink-0" />
                    <div>
                      <h4 className="font-bold text-[#221f1f] uppercase text-[12px] leading-tight">{impact.t}</h4>
                      <p className="text-gray-500 text-[11px] mt-1 leading-normal">{impact.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. WHO WE ARE & WHAT WE DO - Optimized Grid */}
      <section className="py-20 md:py-24 bg-white px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-start">
          {/* Who We Are column */}
          <div>
            <h2 className="text-2xl font-serif font-bold uppercase mb-4 border-b-2 border-[#f9b50b] inline-block pb-1">Who We Are</h2>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              The Illinois Interfaith Organization is a non-profit 501(c)(4) committed to fostering interfaith understanding, unity, and social justice. Our team represents a diverse range of religious and cultural backgrounds, united by a shared commitment to respect, equity, and collaboration.
                <br/><br/>We believe that recognizing our shared humanity is essential to building stronger communities. Through dialogue, education, and service, we create spaces where individuals can learn from one another, engage across differences, and work together toward positive social change. Our efforts focus on advancing inclusivity, strengthening relationships, and promoting a culture of mutual respect.
                         </p>

            <div className="relative group mb-8">
               <img src="/Adam & Eve Day _22.jpg" className="w-full h-72 object-cover border-4 border-[#f9b50b] shadow-xl" alt="Who we are" />
            </div>
            <ul className="space-y-4">
              {["Creating inclusive spaces to learn from one another", "Working together to drive positive social change", "Strengthening relationships through mutual respect"].map((t, i) => (
                <li key={i} className="flex items-center gap-3 text-sm font-medium text-gray-600"><FaCheckSquare className="text-[#f9b50b]" /> {t}</li>
              ))}
            </ul>
          </div>

          {/* What We Do column - More compact grid */}
          <div>
            <h2 className="text-2xl font-serif font-bold uppercase mb-4 border-b-2 border-[#f9b50b] inline-block pb-1 text-[#221f1f]">What We DO</h2>
            <p className="text-gray-500 text-sm mb-8 italic">
The Illinois Interfaith Organization carries out a wide range of initiatives designed to promote interfaith understanding, unity, and social justice. Our programs encourage dialogue, collaboration, and collective action among diverse communities.
              </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { l: "Interfaith Dialogues", i: <FaAnchor />, d: "Facilitated conversations that bring together people of different faiths to share perspectives and build understanding.                  " },
                { l: "Community Outreach", i: <FaChartLine />, d: "Service-oriented programs such as food drives, health initiatives, and educational workshops.                  " },
                { l: "Youth Engagement", i: <FaRss />, d: "Leadership development, cultural exchange, and community service opportunities for young people.              " },
                { l: "Events and Celebrations", i: <FaEdit />, d: "Interfaith services, cultural gatherings, and community-wide events that reflect shared values and diversity." },
                { l: "Advocacy", i: <FaBalanceScale />, d: "Support for policies and initiatives that promote equity, inclusion, and social justice.                  " },
                { l: "Interfaith Education & Capacity Building", i: <FaGraduationCap />, d: " Training programs, seminars, and resource development aimed at equipping faith leaders, volunteers, and community members with the knowledge and skills to address social issues, resolve conflict, and foster sustained collaboration across faith and cultural lines.                  " }
              ].map((item, idx) => (
                <div key={idx} className="p-5 border border-gray-100 flex flex-col items-center text-center transition-all duration-300 hover:border-[#f9b50b] group h-full">
                  <div className="mb-3 text-[#f9b50b] text-2xl group-hover:scale-110 transition-transform">
                    {item.i}
                  </div>
                  <h4 className="font-bold uppercase text-[11px] mb-2 tracking-wider text-[#221f1f]">
                    {item.l}
                  </h4>
                  <p className="text-gray-500 text-[10px] leading-relaxed">
                    {item.d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> 

      {/* 6. TESTIMONIAL SLIDER */}
      <section className="relative bg-[#221f1f] py-20 px-6 overflow-hidden flex items-center">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url('/Community Bash (3).jpeg')`, backgroundSize: 'cover' }} />
        <div className="max-w-4xl mx-auto relative z-10 w-full">
          <div className="relative border border-[#f9b50b]/30 p-8 md:p-16 text-center">
            <div className="flex justify-center mb-6"><span className="text-[#f9b50b] text-6xl font-serif">“</span></div>
            <div className="relative h-48 md:h-32 flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.6 }}
                  className="absolute w-full"
                >
                  <p className="text-white text-base md:text-lg leading-relaxed mb-6 italic opacity-90">{testimonials[currentTestimonial].quote}</p>
                  <div className="w-12 h-0.5 bg-[#f9b50b] mx-auto mb-4"></div>
                  <h4 className="text-white font-serif font-bold uppercase tracking-widest text-sm">
                    {testimonials[currentTestimonial].name} — <span className="text-[#f9b50b]">{testimonials[currentTestimonial].role}</span>
                  </h4>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          <div className="flex justify-center gap-3 mt-10">
            {testimonials.map((_, index) => (
              <button key={index} onClick={() => setCurrentTestimonial(index)} className={`transition-all duration-300 h-2 ${currentTestimonial === index ? 'w-8 bg-[#f9b50b]' : 'w-2 bg-gray-600'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* 7. SUBSCRIBE NOW SECTION */}
      <section className="bg-[#1a1818] py-20 px-6 border-y border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-white text-4xl font-serif font-bold uppercase tracking-wider mb-4">
            Subscribe <span className="text-[#f9b50b]">Now</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base mb-10 font-light max-w-2xl mx-auto leading-relaxed">
            Receive our weekly newsletter with educational materials, upcoming interfaith events,
            and stories of unity from our community.
          </p>
          <form className="flex flex-col md:flex-row items-center justify-center gap-0 max-w-2xl mx-auto shadow-2xl">
            <input
              type="email"
              placeholder="Your Email Address"
              required
              className="w-full md:w-2/3 py-4 px-6 focus:outline-none bg-white text-[#1a1818] text-sm"
            />
            <button
              type="submit"
              className="w-full md:w-1/3 py-4 px-6 bg-[#f9b50b] border-2 border-[#f9b50b] text-[#1a1818] font-bold uppercase tracking-widest hover:bg-transparent hover:text-[#f9b50b] transition-all duration-300 text-xs"
            >
              Subscribe
            </button>
          </form>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] mt-6">
            Unity in Diversity • Illinois Interfaith
          </p>
        </div>
      </section>

      {/* 8. GET IN TOUCH */}
      <section className="pt-20 pb-10 text-center">
        <h2 className="text-5xl font-serif font-bold uppercase text-[#221f1f] mb-2">Get In Touch</h2>
        <p className="text-gray-400 text-sm uppercase tracking-widest font-bold">Contact Us</p>
        <div className="h-[2px] w-16 bg-[#f9b50b] mx-auto mt-6"></div>
      </section>

      <section className="pb-24 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          <div className="lg:col-span-1 space-y-12">
            <div className="flex gap-5">
              <FaMapMarkerAlt className="text-[#f9b50b] text-2xl mt-1" />
              <div>
                <h4 className="font-serif font-bold text-xl uppercase mb-3">Office Address</h4>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=1645+W+Irving+Park+Rd+Suite+100+Hanover+Park+IL+60133"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 text-sm not-italic leading-relaxed hover:text-[#f9b50b] transition-colors"
                >
                  1645 W Irving Park Rd, Suite # 100 <br />
                  Hanover Park, IL, United States, Illinois 60133
                </a>
              </div>
            </div>

            <div className="flex gap-5">
              <FaPhoneAlt className="text-[#f9b50b] text-2xl mt-1" />
              <div>
                <h4 className="font-serif font-bold text-xl uppercase mb-3">Phone</h4>
                <a href="tel:18477377065" className="text-gray-500 text-sm hover:text-[#f9b50b] transition-colors">+1 (847) 737-7065</a>
              </div>
            </div>

            <div className="flex gap-5">
              <FaClock className="text-[#f9b50b] text-2xl mt-1" />
              <div>
                <h4 className="font-serif font-bold text-xl uppercase mb-3">Working Hours</h4>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Monday – Saturday: Always Open <br />
                  (Community inquiries welcomed anytime)
                </p>
              </div>
            </div>

            <div className="flex gap-5">
              <FaEnvelope className="text-[#f9b50b] text-2xl mt-1" />
              <div>
                <h4 className="font-serif font-bold text-xl uppercase mb-3">Email</h4>
                <a href="mailto:info@ilinterfaith.org" className="text-gray-500 text-sm hover:text-[#f9b50b] transition-colors">info@ilinterfaith.org</a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-2xl font-serif font-bold uppercase mb-8">Send Us Email</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <input
                    type="text" name="name" value={formData.name} onChange={handleChange}
                    placeholder="Enter your full name"
                    className={`w-full p-4 bg-gray-50 border ${errors.name ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                  />
                  {errors.name && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.name}</p>}
                </div>
                <div className="space-y-1">
                  <input
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    placeholder="Enter your email"
                    className={`w-full p-4 bg-gray-50 border ${errors.email ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                  />
                  {errors.email && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.email}</p>}
                </div>
              </div>

              <div className="space-y-1">
                <input
                  type="text" name="subject" value={formData.subject} onChange={handleChange}
                  placeholder="Subject"
                  className={`w-full p-4 bg-gray-50 border ${errors.subject ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                />
                {errors.subject && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.subject}</p>}
              </div>

              <div className="space-y-1">
                <textarea
                  name="message" rows="8" value={formData.message} onChange={handleChange}
                  placeholder="Write your message"
                  className={`w-full p-4 bg-gray-50 border ${errors.message ? 'border-red-500' : 'border-gray-100'} focus:outline-none focus:bg-white focus:border-[#f9b50b] transition-all text-sm`}
                ></textarea>
                {errors.message && <p className="text-red-500 text-[10px] uppercase font-bold pl-1">{errors.message}</p>}
              </div>

              <button
                disabled={isSubmitting}
                className="bg-[#221f1f] text-white font-bold uppercase tracking-widest px-10 py-4 hover:bg-[#221f1f] transition-all disabled:bg-gray-400 mt-4 text-xs shadow-lg"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>

              {successMsg && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-green-50 border border-green-200"
                >
                  <p className="text-green-700 font-bold text-center text-sm uppercase tracking-tight">
                    {successMsg}
                  </p>
                </motion.div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;